import { mergeExchangeStatesByKey, splitRegistryElementPersistName } from '@/common/utils/normalizeExchangeStates'
import { normalizeDispatchedVisibilities } from '@/services/action/applyDispatchedVisibilities'
import { findLayoutPageTabByVisibilityName } from '@/common/utils/pageTabMeta'
import { isTilesRowScopedRegistryName } from '@/common/utils/tilesSetInlineVisibility'
import { resolveNameBasedElementVisibilityTarget } from '@/common/utils/resolveNameBasedElementVisibility'
import { resolveFormSchemaIdFromPageEntry } from '@/common/utils/resolvePageIdentity'
import type { AppStore } from '@/stores/app'

export type VisibilityStatePatch = {
  element_id?: string | number
  element_name?: string
  element_level?: string
  is_visible?: boolean
  is_editable?: boolean
}

function normalizeVisibilityLevel(level: string): string {
  const normalized = String(level ?? '').trim().toUpperCase()
  if (normalized === 'PANEL' || normalized === 'PAGE' || normalized === 'ELEMENT') {
    return normalized
  }
  if (normalized === 'OVERLAYPAGE') {
    return 'OVERLAYPAGE'
  }
  return normalized || 'ELEMENT'
}

function persistNameFields(name: string): Pick<VisibilityStatePatch, 'element_name' | 'element_id'> {
  const { fieldName, xmlId } = splitRegistryElementPersistName(name)
  if (!xmlId) return { element_name: fieldName }
  const asNumber = Number(xmlId)
  return {
    element_name: fieldName,
    element_id: Number.isFinite(asNumber) && asNumber > 0 ? asNumber : xmlId,
  }
}

function visibilityPatchMatchKeys(patch: {
  element_id?: unknown
  element_name?: unknown
  element_level?: unknown
}): string[] {
  const level = String(patch.element_level ?? 'ELEMENT').trim().toUpperCase() || 'ELEMENT'
  const keys: string[] = []
  const id = patch.element_id != null ? String(patch.element_id).trim() : ''
  const name = String(patch.element_name ?? '').trim()
  if (id) keys.push(`${level}:${id}`)
  if (name) keys.push(`${level}::${name}`)
  return keys
}

function isStaleEditableFalse(value: unknown): boolean {
  if (value === false || value === 0) return true
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase()
    return text === 'false' || text === '0'
  }
  return false
}

/**
 * `setVisible(true)` must not write `is_editable`. After merge, drop hide-pairing
 * `is_editable:false` so XML / explicit `setEditable` apply. Leave `is_editable:true`.
 */
export function dropStaleEditableAfterVisibilityShow(
  merged: unknown[] | null | undefined,
  patches: VisibilityStatePatch[] | null | undefined,
): any[] {
  const states = Array.isArray(merged) ? merged : []
  if (states.length === 0 || !patches?.length) return states

  const keepEditableKeys = new Set<string>()
  const dropKeys = new Set<string>()
  for (const patch of patches) {
    const level = String(patch.element_level ?? 'ELEMENT').trim().toUpperCase()
    if (level !== 'ELEMENT' && level !== '') continue
    const keys = visibilityPatchMatchKeys(patch)
    if (patch.is_editable !== undefined) {
      for (const key of keys) keepEditableKeys.add(key)
      continue
    }
    if (patch.is_visible === true) {
      for (const key of keys) dropKeys.add(key)
    }
  }
  if (dropKeys.size === 0) return states

  return states.map((state) => {
    if (!state || typeof state !== 'object' || Array.isArray(state)) return state
    const row = state as Record<string, unknown>
    const keys = visibilityPatchMatchKeys(row)
    const shouldDrop =
      keys.some((key) => dropKeys.has(key)) && !keys.some((key) => keepEditableKeys.has(key))
    if (!shouldDrop || !isStaleEditableFalse(row.is_editable)) return state
    const next = { ...row }
    delete next.is_editable
    return next
  })
}

/**
 * Запись патча для merge в jsResult.states (onLoad pre-build, chainCalled visibility).
 * Show/hide пишут только `is_visible` — `setVisible` не меняет editable (Swing).
 */
export function recordVisibilityStatePatch(
  patches: VisibilityStatePatch[],
  input: {
    level?: string
    id?: string | number | null
    name?: string
    visible: boolean
  },
): void {
  const level = input.level ? normalizeVisibilityLevel(input.level) : 'ELEMENT'

  if (input.level && input.id != null && input.id !== '') {
    const idStr = String(input.id).trim()
    if (level === 'ELEMENT' && idStr !== '' && !/^\d+$/.test(idStr)) {
      patches.push({
        ...persistNameFields(idStr),
        element_level: level,
        is_visible: input.visible,
      })
      return
    }
    patches.push({
      element_id: input.id,
      element_level: level,
      is_visible: input.visible,
    })
    return
  }

  const name = String(input.name ?? '').trim()
  if (!name) return

  const nameLevel =
    level === 'PAGE' || level === 'PANEL' || level === 'ACTION' || level === 'OVERLAYPAGE'
      ? level
      : 'ELEMENT'

  if (/^\d+$/.test(name)) {
    patches.push({
      element_id: name,
      element_level: nameLevel,
      is_visible: input.visible,
    })
    return
  }

  patches.push({
    ...(nameLevel === 'ELEMENT' ? persistNameFields(name) : { element_name: name }),
    element_level: nameLevel,
    is_visible: input.visible,
  })
}

/**
 * Two-arg `en.setVisible(name, visible)` / dispatched `{ level: name, id: null }`.
 * Layout tab (jsName / xml id / title) → PAGE; otherwise ELEMENT.
 */
export function recordSetVisibleNamePatch(
  patches: VisibilityStatePatch[],
  name: string | number | null | undefined,
  visible: boolean,
  layoutForm?: unknown,
): boolean {
  const idStr = String(name ?? '').trim()
  if (!idStr) return false
  const match = findLayoutPageTabByVisibilityName(layoutForm, idStr)
  if (match) {
    patches.push(
      ...buildClientPageVisibilityPatches({
        pageId: match.id || undefined,
        pageName: match.jsName || undefined,
        visible,
      }),
    )
    return true
  }
  recordVisibilityStatePatch(patches, { name: idStr, visible })
  return false
}

/** `en.setVisible(level, id, visible)` — PAGE looks up layout tabs by id or jsName. */
export function recordSetVisibleByLevelPatch(
  patches: VisibilityStatePatch[],
  level: string,
  id: string | number,
  visible: boolean,
  layoutForm?: unknown,
): void {
  const levelNorm = String(level ?? '').trim().toLowerCase()
  if (levelNorm === 'page') {
    const idStr = String(id ?? '').trim()
    const match = findLayoutPageTabByVisibilityName(layoutForm, idStr)
    if (match) {
      patches.push(
        ...buildClientPageVisibilityPatches({
          pageId: match.id || undefined,
          pageName: match.jsName || undefined,
          visible,
        }),
      )
      return
    }
    recordVisibilityStatePatch(patches, { level: 'PAGE', id, visible })
    return
  }
  recordVisibilityStatePatch(patches, { level, id, visible })
}

/**
 * Two-arg `en.setEditable(name, editable)` / dispatched `{ level: name, id: null }`.
 */
export function recordSetEditableNamePatch(
  patches: VisibilityStatePatch[],
  name: string | number | null | undefined,
  editable: boolean,
): void {
  const idStr = String(name ?? '').trim()
  if (!idStr) return
  if (shouldSkipTilesInlineVisibilityPageStorePersist(idStr)) return
  if (/^\d+$/.test(idStr)) {
    patches.push({
      element_id: idStr,
      element_level: 'ELEMENT',
      is_editable: editable,
    })
    return
  }
  patches.push({
    element_name: idStr,
    element_level: 'ELEMENT',
    is_editable: editable,
  })
}

/** `en.setEditable(level, id, editable)` — PANEL/ELEMENT persist without requiring is_visible. */
export function recordSetEditableByLevelPatch(
  patches: VisibilityStatePatch[],
  level: string,
  id: string | number,
  editable: boolean,
): void {
  const levelNorm = normalizeVisibilityLevel(level)
  const idStr = String(id ?? '').trim()
  if (!idStr) return
  if (levelNorm === 'ELEMENT' && !/^\d+$/.test(idStr)) {
    if (shouldSkipTilesInlineVisibilityPageStorePersist(idStr)) return
    patches.push({
      element_name: idStr,
      element_level: 'ELEMENT',
      is_editable: editable,
    })
    return
  }
  patches.push({
    element_id: id,
    element_level: levelNorm || 'ELEMENT',
    is_editable: editable,
  })
}

export type DispatchedEditable = {
  level: string
  id?: number | string | null
  editable: boolean
}

export function normalizeDispatchedEditables(raw: unknown): DispatchedEditable[] {
  if (!raw || !Array.isArray(raw)) return []
  const result: DispatchedEditable[] = []
  for (const entry of raw) {
    if (entry == null || typeof entry !== 'object') continue
    const obj = entry as Record<string, unknown>
    if (!('editable' in obj) && !('level' in obj)) continue
    result.push({
      level: String(obj.level ?? ''),
      id: obj.id == null ? null : (obj.id as number | string),
      editable: obj.editable === true || obj.editable === 'true',
    })
  }
  return result
}

export function editablePatchesFromDispatched(raw: unknown): VisibilityStatePatch[] {
  const patches: VisibilityStatePatch[] = []
  for (const item of normalizeDispatchedEditables(raw)) {
    if (item.id == null || item.id === '') {
      recordSetEditableNamePatch(patches, item.level, item.editable)
      continue
    }
    recordSetEditableByLevelPatch(patches, item.level, item.id, item.editable)
  }
  return patches
}

export function visibilityPatchesFromDispatched(
  raw: unknown,
  layoutForm?: unknown,
  pageData?: unknown,
  slotId?: string,
): VisibilityStatePatch[] {
  const patches: VisibilityStatePatch[] = []
  const normalized = normalizeDispatchedVisibilities(raw)
  for (const item of normalized) {
    if (item.id == null || item.id === '') {
      recordSetVisibleNamePatch(patches, item.level, item.visible, layoutForm)
      continue
    }
    recordSetVisibleByLevelPatch(patches, item.level, item.id, item.visible, layoutForm)
  }
  const page =
    pageData && typeof pageData === 'object' && !Array.isArray(pageData)
      ? (pageData as Record<string, unknown>)
      : layoutForm && typeof layoutForm === 'object'
        ? { form: layoutForm }
        : undefined
  return enrichElementVisibilityPatchesWithXmlIds(patches, page, slotId)
}

/**
 * Tiles `setInlineVisibility` chrome (`table__rowN__name`) must not land in `jsResult.states`.
 * SSOT is the tiles row-map + scoped registry + `persistentScopedElementVisibility`.
 * Persisting each call replaces the whole page slot and storms every tile clone (form 2573).
 */
export function shouldSkipTilesInlineVisibilityPageStorePersist(
  elementName?: string | null,
): boolean {
  return isTilesRowScopedRegistryName(String(elementName ?? '').trim())
}

export function omitTilesRowScopedVisibilityPatches(
  patches: VisibilityStatePatch[],
): VisibilityStatePatch[] {
  if (!patches.length) return patches
  return patches.filter(
    (patch) => !shouldSkipTilesInlineVisibilityPageStorePersist(patch.element_name),
  )
}

export function buildClientElementVisibilityPatches(input: {
  elementId?: string | number | null
  elementName?: string | null
  visible: boolean
}): VisibilityStatePatch[] {
  const patches: VisibilityStatePatch[] = []
  const elementId = input.elementId
  const elementName = String(input.elementName ?? '').trim()
  // Scoped tiles name + numeric clone id would otherwise persist as a global ELEMENT state.
  if (shouldSkipTilesInlineVisibilityPageStorePersist(elementName)) {
    return []
  }
  const idStr = elementId != null ? String(elementId).trim() : ''

  if (idStr !== '' && /^\d+$/.test(idStr)) {
    recordVisibilityStatePatch(patches, { level: 'ELEMENT', id: elementId!, visible: input.visible })
  }
  if (elementName !== '' && !/^\d+$/.test(elementName)) {
    recordVisibilityStatePatch(patches, { name: elementName, visible: input.visible })
  }
  return patches
}

export function buildClientPageVisibilityPatches(input: {
  pageId?: string | number | null
  pageName?: string | null
  visible: boolean
}): VisibilityStatePatch[] {
  const patches: VisibilityStatePatch[] = []
  const pageId = input.pageId != null ? String(input.pageId).trim() : ''
  const pageName = String(input.pageName ?? '').trim()
  if (pageId) {
    recordVisibilityStatePatch(patches, {
      level: 'PAGE',
      id: input.pageId,
      visible: input.visible,
    })
  }
  if (pageName && pageName !== pageId) {
    recordVisibilityStatePatch(patches, {
      level: 'PAGE',
      name: pageName,
      visible: input.visible,
    })
  }
  return patches
}

function readNumericElementXmlId(value: unknown): string | number | null {
  if (value == null || value === '') return null
  const text = String(value).trim()
  if (!/^\d+$/.test(text)) return null
  const asNumber = Number(text)
  return Number.isFinite(asNumber) && asNumber > 0 ? asNumber : text
}

/** Name-based ELEMENT persist must carry platform xml id so merge hits onLoad ELEMENT:<id>. */
export function enrichElementVisibilityPatchesWithXmlIds(
  patches: VisibilityStatePatch[],
  pageData: Record<string, unknown> | null | undefined,
  slotId?: string,
): VisibilityStatePatch[] {
  if (!pageData || patches.length === 0) return patches
  const schemaId = resolveFormSchemaIdFromPageEntry(pageData, slotId)
  const registry =
    schemaId > 0
      ? String(schemaId)
      : String(slotId ?? '').trim() || (pageData.form ? 'form' : '')
  if (!registry) return patches

  return patches.map((patch) => {
    const level = String(patch.element_level ?? 'ELEMENT').toUpperCase()
    if (level !== 'ELEMENT' && level !== '') return patch
    if (readNumericElementXmlId(patch.element_id) != null) return patch
    const name = String(patch.element_name ?? '').trim()
    if (!name || /^\d+$/.test(name)) return patch
    const target = resolveNameBasedElementVisibilityTarget({
      name,
      formId: registry,
      pageData,
    })
    const xmlId = readNumericElementXmlId(target?.elementId)
    if (xmlId == null) return patch
    return { ...patch, element_id: xmlId, element_level: 'ELEMENT' }
  })
}

export function persistVisibilityStatePatchesToPageStore(
  appStore: AppStore | undefined,
  slotId: string | undefined,
  patches: VisibilityStatePatch[],
  setPageDataSource: string,
): void {
  if (!appStore || !slotId || patches.length === 0) return
  const persistable = omitTilesRowScopedVisibilityPatches(patches)
  if (persistable.length === 0) return
  const pages = appStore.pagesData as Record<string, unknown>
  const page = pages[slotId] as Record<string, any> | undefined
  if (!page) return

  const prevStates = Array.isArray(page.jsResult?.states) ? page.jsResult.states : []
  const enriched = enrichElementVisibilityPatchesWithXmlIds(persistable, page, slotId)
  const mergedStates = dropStaleEditableAfterVisibilityShow(
    mergeExchangeStatesByKey(prevStates, enriched),
    enriched,
  )

  appStore.setPageData(
    slotId,
    {
      ...page,
      jsResult: {
        ...page.jsResult,
        states: mergedStates,
      },
    },
    undefined,
    undefined,
    setPageDataSource,
  )
}

/**
 * Slot for client setVisible → jsResult.states persist.
 * Prefer En/dialog pageDataSlot over ambient currentPageId (startup NEW_DIALOG).
 */
export function resolveVisibilityPersistSlotId(input: {
  overrideSlotId?: string | null
  enPageDataSlotId?: string | null
  currentPageSlotId?: string | null
  /** Map candidate → existing pagesData key; empty string if missing */
  resolveExistingSlot?: (candidate: string) => string
}): string {
  const resolve = input.resolveExistingSlot ?? ((s: string) => String(s ?? '').trim())
  for (const candidate of [input.overrideSlotId, input.enPageDataSlotId, input.currentPageSlotId]) {
    const raw = String(candidate ?? '').trim()
    if (!raw) continue
    const resolved = resolve(raw)
    if (resolved) return resolved
  }
  return ''
}

/**
 * Guard: do not let stale states `is_visible:false` undo client shell true
 * (e.g. hasWebsite showed table; later refill/runSiteCheck reapplied open-dialog false).
 *
 * `is_refill` / `is_empty` are NOT authoritative for visibility — middle refillElement
 * often rides on the same state row that still carries open-time is_visible:false.
 * Dedicated ELEMENT hide (`is_visible:false` without refill/empty) must apply — checkbox
 * setVisible after onLoad already set Label shell true.
 * Client hide updates shell to false first, so this guard does not block a real hide.
 */
export function shouldApplyIncomingElementVisibility(input: {
  incomingVisible: boolean
  shellVisible?: boolean
  isRefill?: boolean
  isEmpty?: boolean
}): boolean {
  if (input.shellVisible === true && input.incomingVisible === false) {
    if (input.isRefill === true || input.isEmpty === true) {
      return false
    }
  }
  return true
}

/** Restore Label shell from ELEMENT xml ids that persist into jsResult.states. */
export function applyElementVisibilityPatchesToShell(
  patches: VisibilityStatePatch[] | null | undefined,
  setElementShellVisibleById?: (elementId: string | number, visible: boolean) => void,
): void {
  if (typeof setElementShellVisibleById !== 'function') return
  for (const patch of patches ?? []) {
    if (String(patch?.element_level ?? '').trim().toUpperCase() !== 'ELEMENT') continue
    if (typeof patch.is_visible !== 'boolean') continue
    const id = patch.element_id
    if (id == null || id === '') continue
    const idStr = String(id).trim()
    if (!/^\d+$/.test(idStr)) continue
    setElementShellVisibleById(id, patch.is_visible)
  }
}
